// Consent-gated analytics. A thin abstraction so the vendor is swappable (Umami today,
// PostHog later) without touching call sites. The Umami script is injected ONLY after the
// visitor grants analytics consent, and removed if consent is withdrawn.
//
// IMPORTANT: never pass patient PII or health data to trackEvent — analytics must stay
// free of special-category data.

import { getConsent, onConsentChange } from './consent';

export interface UmamiConfig {
  url: string; // base URL of the self-hosted Umami instance
  websiteId: string; // Umami website id
}

declare global {
  interface Window {
    umami?: { track: (event?: string, data?: Record<string, unknown>) => void };
  }
}

const SCRIPT_ID = 'umami-analytics';
let loaded = false;

function loadScript(config: UmamiConfig): void {
  if (loaded || typeof document === 'undefined' || document.getElementById(SCRIPT_ID)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.src = `${config.url.replace(/\/$/, '')}/script.js`;
  script.setAttribute('data-website-id', config.websiteId);
  script.setAttribute('data-do-not-track', 'true');
  document.head.appendChild(script);
  loaded = true;
}

function unloadScript(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(SCRIPT_ID)?.remove();
  delete window.umami;
  loaded = false;
}

/**
 * Wires up consent-gated Umami. No-op when config is missing (e.g. the Umami server
 * isn't deployed yet) — so it's safe to call always; analytics simply stays off until
 * both the config is present AND the visitor consents.
 */
export function initAnalytics(config?: Partial<UmamiConfig>): void {
  if (!config?.url || !config?.websiteId) return;
  const cfg = config as UmamiConfig;

  const apply = () => {
    if (getConsent()?.analytics) loadScript(cfg);
    else unloadScript();
  };

  apply();
  onConsentChange(apply);
}

/** Track a custom event. Guarded + no-op until Umami is loaded. Never pass patient PII. */
export function trackEvent(name: string, data?: Record<string, unknown>): void {
  if (typeof window !== 'undefined') window.umami?.track(name, data);
}
